import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Download, RefreshCw, LogOut, Filter } from "lucide-react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { useDebounce } from "@/lib/useDebounce";
import { PuzzleMark } from "@/components/puzzle/PuzzleMark";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import LeadDetailSlideOver from "@/components/admin/LeadDetailSlideOver";
import StatsStrip from "@/components/admin/StatsStrip";

const SERVICE_FILTERS = [
  "All",
  "Print Media",
  "Corporate Gifting",
  "Signage & Display",
  "Promotional Products",
  "Brand Development Support",
  "Digital & Media Extension",
];

const fieldStyle = {
  background: "transparent",
  border: "1px solid rgba(192,192,192,0.2)",
  color: "#F5F1E8",
  fontFamily: "Montserrat",
  fontSize: 13,
  padding: "10px 14px",
  height: 40,
  borderRadius: 0,
};

const cellStyle = {
  fontFamily: "Montserrat",
  fontSize: 13,
  padding: "14px 14px",
  color: "#F5F1E8",
  borderBottom: "1px solid rgba(192,192,192,0.08)",
  verticalAlign: "top",
};

const headerStyle = {
  fontFamily: "Montserrat",
  fontSize: 10,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  textAlign: "left",
  color: "#B89B5E",
  padding: "12px 14px",
  borderBottom: "1px solid rgba(192,192,192,0.22)",
  whiteSpace: "nowrap",
  fontWeight: 600,
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const AdminLeads = () => {
  const { admin, logout } = useAdminAuth();
  const [q, setQ] = useState("");
  const [service, setService] = useState("All");
  const [source, setSource] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeLead, setActiveLead] = useState(null);
  const [error, setError] = useState("");
  const [statsKey, setStatsKey] = useState(0);

  // 300ms debounce for free-text filters so we don't hit the API on each keystroke
  const debouncedQ = useDebounce(q, 300);
  const debouncedSource = useDebounce(source, 300);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (debouncedQ.trim()) p.set("q", debouncedQ.trim());
    if (service && service !== "All") p.set("service", service);
    if (debouncedSource.trim()) p.set("source", debouncedSource.trim());
    return p;
  }, [debouncedQ, service, debouncedSource]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/leads?${queryParams.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleExport = useCallback(async () => {
    try {
      const token = getToken();
      const url = `${API_BASE}/admin/leads/export?${queryParams.toString()}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `krister-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      setError(e.message || "Export failed");
    }
  }, [queryParams]);

  return (
    <main
      data-testid="admin-leads-page"
      style={{
        background: "#0A0A0A",
        color: "#F5F1E8",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      {/* header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          background: "rgba(10,10,10,0.92)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(192,192,192,0.15)",
          zIndex: 30,
        }}
      >
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <a
            href="/"
            style={{ display: "flex", alignItems: "center", gap: 12, color: "#F5F1E8", textDecoration: "none" }}
          >
            <PuzzleMark size={30} animated={false} />
            <div>
              <div
                style={{
                  fontWeight: 900,
                  letterSpacing: "0.16em",
                  fontSize: 12,
                  color: "#F5F1E8",
                }}
              >
                KRISTER OVERSEAS
              </div>
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: "0.26em",
                  color: "#B89B5E",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                Admin · Leads
              </div>
            </div>
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: 11,
                letterSpacing: "0.22em",
                color: "#C0C0C0",
                textTransform: "uppercase",
              }}
              data-testid="admin-current-email"
            >
              {admin?.email}
            </span>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={logout}
              style={{
                ...fieldStyle,
                height: 36,
                padding: "0 14px",
                cursor: "pointer",
                background: "transparent",
                color: "#F5F1E8",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
              }}
            >
              <LogOut size={14} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "32px 24px 64px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "end",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "Montserrat",
                fontWeight: 800,
                fontSize: "clamp(32px, 4vw, 48px)",
                letterSpacing: "-0.02em",
                lineHeight: 1,
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              Leads
            </h1>
            <div
              style={{
                marginTop: 8,
                color: "#C0C0C0",
                fontSize: 13,
                letterSpacing: "0.04em",
              }}
              data-testid="admin-leads-count"
            >
              {loading ? "Loading…" : `${total} lead${total === 1 ? "" : "s"}`}
              {q || service !== "All" || source ? " · filtered" : ""}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              data-testid="admin-refresh"
              onClick={() => {
                fetchLeads();
                setStatsKey((k) => k + 1);
              }}
              style={{
                ...fieldStyle,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <RefreshCw size={14} strokeWidth={1.5} /> Refresh
            </button>
            <button
              type="button"
              data-testid="admin-export-csv"
              onClick={handleExport}
              style={{
                background: "#F5F1E8",
                color: "#0A0A0A",
                fontFamily: "Montserrat",
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                fontWeight: 700,
                padding: "0 18px",
                height: 40,
                border: "none",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Download size={14} strokeWidth={1.5} /> Export CSV
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <StatsStrip refreshKey={statsKey} />

        {/* filters */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(220px, 2fr) minmax(180px, 1fr) minmax(160px, 1fr)",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              strokeWidth={1.5}
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#C0C0C0",
              }}
            />
            <Input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, company, message…"
              style={{ ...fieldStyle, paddingLeft: 38 }}
            />
          </div>
          <Select value={service} onValueChange={setService}>
            <SelectTrigger style={fieldStyle} data-testid="admin-filter-service">
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent
              style={{
                background: "#0A0A0A",
                border: "1px solid rgba(192,192,192,0.25)",
                color: "#F5F1E8",
                borderRadius: 0,
              }}
            >
              {SERVICE_FILTERS.map((s) => (
                <SelectItem key={s} value={s} style={{ color: "#F5F1E8" }}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div style={{ position: "relative" }}>
            <Filter
              size={14}
              strokeWidth={1.5}
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#C0C0C0",
              }}
            />
            <Input
              data-testid="admin-filter-source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="UTM source (e.g. google)"
              style={{ ...fieldStyle, paddingLeft: 38 }}
            />
          </div>
        </div>

        {error && (
          <div
            data-testid="admin-error"
            style={{
              padding: "12px 14px",
              background: "rgba(214,0,127,0.08)",
              border: "1px solid rgba(214,0,127,0.35)",
              color: "#F5F1E8",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* table */}
        <div
          style={{
            border: "1px solid rgba(192,192,192,0.15)",
            overflowX: "auto",
            background: "#0F0F0F",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 920,
            }}
            data-testid="admin-leads-table"
          >
            <thead>
              <tr>
                <th style={headerStyle}>Submitted</th>
                <th style={headerStyle}>Name</th>
                <th style={headerStyle}>Email</th>
                <th style={headerStyle}>Company</th>
                <th style={headerStyle}>Service</th>
                <th style={headerStyle}>Budget</th>
                <th style={headerStyle}>Source</th>
                <th style={headerStyle}></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} style={{ ...cellStyle, textAlign: "center", color: "#C0C0C0", padding: "40px" }}>
                    No leads match your filters.
                  </td>
                </tr>
              )}
              {items.map((lead) => (
                <tr
                  key={lead.id}
                  data-testid={`lead-row-${lead.id}`}
                  onClick={() => setActiveLead(lead)}
                  style={{
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#141414")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ ...cellStyle, color: "#C0C0C0", fontSize: 12 }}>{formatDate(lead.submittedAt)}</td>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{lead.name}</td>
                  <td style={{ ...cellStyle, color: "#C0C0C0" }}>{lead.email}</td>
                  <td style={cellStyle}>{lead.company || "—"}</td>
                  <td style={cellStyle}>{lead.serviceInterestedIn || "—"}</td>
                  <td style={cellStyle}>{lead.budget || "—"}</td>
                  <td style={{ ...cellStyle, color: "#B89B5E", fontFamily: "ui-monospace, SF Mono, monospace", fontSize: 12 }}>
                    {lead.last_utm_source || lead.first_utm_source || "direct"}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>
                    <span
                      style={{
                        fontSize: 10,
                        letterSpacing: "0.22em",
                        color: "#B89B5E",
                        textTransform: "uppercase",
                      }}
                    >
                      View →
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LeadDetailSlideOver
        lead={activeLead}
        open={!!activeLead}
        onClose={() => setActiveLead(null)}
      />
    </main>
  );
};

export default AdminLeads;
